import React, { useCallback, useEffect, useState } from 'react';

import { decode } from 'html-entities';
import { Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { useAppSelector } from 'hooks';
import { useAxios } from 'configs/requests';
import { Loading, Error as FetchError } from 'components/Fetch';
import {
  SmartEventActionType,
  SmartEventConditionType,
  SmartEventDateTarget,
  SmartEventValueType
} from 'constants/enums';
import { getQuestionnaires } from 'store/groups/reducer';

import ReviewStep from './ReviewStep';

const splitTimeWindow = (timeWindowCode?: string) => {
  if (!timeWindowCode) {
    return [undefined, undefined];
  }
  return [timeWindowCode.slice(0, -1), timeWindowCode.at(-1)];
};

interface ReviewStepWrapperProps {
  close: () => void;
  attribute: string;
}

const ReviewStepWrapperModal = ({ close, attribute }: ReviewStepWrapperProps) => {
  const intl = useIntl();
  const groupQuestionnaires = useAppSelector(getQuestionnaires);

  const [description, setDescription] = useState<string>('');
  const [conditions, setConditions] = useState<ISmartEventConditionForm[]>([]);
  const [actions, setActions] = useState<ISmartEventActionForm[]>([]);
  const [managedSmartEvent, setManagedSmartEvent] = useState<IGetSmartEvent>();

  const [{ data, loading, error }, get] = useAxios({
    url: `/clinician/PrivateSmartEvent/${attribute}`
  });

  const parseCondition = (condition: string) => {
    switch (condition) {
      case 'neq':
        return '0';
      case 'eq':
        return '1';
      case 'lt':
        return '2';
      case 'lte':
        return '3';
      case 'gt':
        return '4';
      case 'gte':
        return '5';
      default:
        return condition;
    }
  };

  const getTitleFromSurveyId = useCallback(
    (surveyId: string): string => {
      return (
        groupQuestionnaires.find((questionnaire: ISurvey) => questionnaire.survey_id === surveyId)
          ?.title || surveyId
      );
    },
    [groupQuestionnaires]
  );

  const parseConditions = useCallback((conditions: any[]): ISmartEventConditionForm[] => {
    return conditions.map((condition) => {
      const conditionType =
        condition.target_type === 'observation_value_pct'
          ? SmartEventConditionType.MEASUREMENT
          : condition.target_type;
      const [timeWindowValue, timeWindowUnit] = splitTimeWindow(condition.time_window);
      return {
        conditionType: conditionType,
        measurementClassCondition: [
          SmartEventConditionType.MEASUREMENT,
          SmartEventConditionType.BASELINE
        ].includes(conditionType)
          ? condition.target
          : '',
        baselineComponent: condition.component || '',
        questionnaireCondition:
          conditionType === SmartEventConditionType.QUESTIONNAIRE ? condition.target : '',
        dateTarget: conditionType === SmartEventConditionType.DATE ? condition.target : '',
        complianceTarget:
          conditionType === SmartEventConditionType.COMPLIANCE ? condition.complianceTarget : '',
        profileTarget:
          conditionType === SmartEventConditionType.PROFILE ? condition.profileTarget : '',
        condition: parseCondition(condition.condition),
        valueType:
          condition.target_type === 'observation_value_pct'
            ? SmartEventValueType.PERCENTAGE
            : SmartEventValueType.ABSOLUTE,
        value:
          condition.target === SmartEventDateTarget.PREGNANCY_WEEKS
            ? condition.value / 7
            : condition.value,
        valueUnit: condition.unit,
        evalTime: condition.eval_time || undefined,
        timeWindowValue,
        timeWindowUnit
      };
    });
  }, []);

  const parseActions = useCallback(
    (actions: any[]): ISmartEventActionForm[] => {
      return actions.map((action) => {
        return {
          actionType: action.action_type,
          alertType: action.properties.alert_type,
          alertLevel: action.properties.alert_level || '',
          alertTitle: decode(action.properties.alert_title) || '',
          alertDescription: decode(action.properties.alert_text) || '',
          questionnaireAction:
            action.action_type === SmartEventActionType.QUESTIONNAIRE
              ? getTitleFromSurveyId(action.properties.survey_id)
              : '',
          measurementClassAction: action.properties.measurement_class || '',
          messageAction:
            action.action_type === SmartEventActionType.MESSAGE
              ? decode(action.properties.message_text)
              : ''
        };
      });
    },
    [getTitleFromSurveyId]
  );

  useEffect(() => {
    if (data) {
      if (data.attribute.startsWith('tag_priv_')) {
        const smartEvent = JSON.parse(data.ae_rule_arg_json);
        setDescription(smartEvent.summary);
        setConditions(parseConditions(smartEvent.conditions));
        setActions(parseActions(smartEvent.actions));
      } else {
        setManagedSmartEvent(data);
      }
    }
  }, [data, parseConditions, parseActions]);

  return (
    <>
      {!managedSmartEvent && error ? (
        <FetchError retry={get} />
      ) : loading ? (
        <Loading />
      ) : (
        <Modal data-test="smart-event-view-modal" show onHide={() => close()} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{intl.formatMessage({ id: 'smartEventDescriptionTitleKey' })}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ReviewStep
              hideReview={true}
              conditions={conditions}
              actions={actions}
              description={description}
              managedSmartEvent={managedSmartEvent}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              data-test="smart-event-view-modal-close-btn"
              variant="link"
              onClick={() => close()}
            >
              {intl.formatMessage({ id: 'closeKey' })}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default ReviewStepWrapperModal;
